/**
 * Vier-Augen-Prinzip der Pflichtenprüfung — Spiegel von tri-server `legalReview.ts`.
 *
 * Erst gibt ein Fachprüfer Recht (Plattformrolle `legalReviewer`) frei, dann ein Fachprüfer FM
 * (`fmReviewer`), zwei verschiedene Personen. Der Server entscheidet; die Maske zeigt nur vorab,
 * welche Stufe offen ist und warum der angemeldete Nutzer sie (nicht) freigeben kann.
 */

import { useAtomValue } from "jotai";
import { useTranslation } from "react-i18next";

import { authenticationAtom } from "../../State";
import { myPlatformRolesAtom } from "../../PlatformRolesAPI";
import { formatDateTime } from "./legalDisplay";

export type ReviewerRole = "legal" | "fm";

export const REVIEWER_USER_ROLES: Record<ReviewerRole, string> = {
  legal: "legalReviewer",
  fm: "fmReviewer",
};

const OTHER_ROLE: Record<ReviewerRole, ReviewerRole> = { legal: "fm", fm: "legal" };

export interface ReviewerIdentity {
  email?: string;
  roles?: string[];
}

export const reviewerRolesOf = (reviewer: ReviewerIdentity): ReviewerRole[] =>
  (Object.keys(REVIEWER_USER_ROLES) as ReviewerRole[]).filter((role) =>
    (reviewer?.roles || []).includes(REVIEWER_USER_ROLES[role]),
  );

/** Whose approval the version waits for — null once it is confirmed. */
export const pendingApprovalRole = (obligation: any): ReviewerRole | null => {
  if (obligation?.reviewStatus === "confirmed") return null;
  if (obligation?.reviewStatus === "legalConfirmed") return "fm";
  return "legal";
};

export type ApprovalBlock = "alreadyConfirmed" | "missingRole" | "samePerson";

export const approvalBlock = (obligation: any, reviewer: ReviewerIdentity): ApprovalBlock | null => {
  const role = pendingApprovalRole(obligation);
  if (!role) return "alreadyConfirmed";
  if (!reviewerRolesOf(reviewer).includes(role)) return "missingRole";
  const other = obligation?.approvals?.[OTHER_ROLE[role]];
  if (other?.by && other.by === reviewer?.email) return "samePerson";
  return null;
};

/** The signed-in reviewer: email from the login, roles from the platform roles (not user.roles). */
export const useReviewer = (): ReviewerIdentity => {
  const authentication: any = useAtomValue(authenticationAtom);
  const platformRoles = useAtomValue(myPlatformRolesAtom);
  return { email: authentication?.email, roles: platformRoles || [] };
};

/** Review state of one version as seen by the signed-in reviewer. */
export const useFourEyes = (obligation: any) => {
  const reviewer = useReviewer();
  const stage = pendingApprovalRole(obligation);
  const block = obligation ? approvalBlock(obligation, reviewer) : null;
  return {
    stage,
    block,
    canApprove: !!obligation && block === null,
    canVeto: reviewerRolesOf(reviewer).length > 0,
  };
};

/** Button label of the approval stage the signed-in reviewer would give. */
export const approveLabel = (t: (key: string) => string, stage: ReviewerRole | null, withChanges: boolean) =>
  stage
    ? t(`operatorResponsibilityPanel.fourEyes.approve${withChanges ? "WithChanges" : ""}.${stage}`)
    : t(withChanges ? "operatorResponsibilityPanel.confirmWithChanges" : "operatorResponsibilityPanel.confirm");

export type ReviewDialogAction = "confirm" | "needsRevision" | "reject";

/**
 * Title, consequence and submit label of a review dialog. A clean approval stays on the version
 * (only an edit appends one), and it names its stage — the dialog used to say "Mit Anpassungen
 * bestätigen … als neue Version angehängt" for every approval, which is what the server does
 * only for an edit.
 */
export const reviewDialogTexts = (
  t: (key: string, options?: Record<string, unknown>) => string,
  action: ReviewDialogAction,
  stage: ReviewerRole | null,
  withChanges: boolean,
  obligationKey: string,
) => {
  if (action !== "confirm") {
    return {
      title: t(`operatorResponsibilityPanel.reviewNoteTitle.${action}`),
      consequence: t(`operatorResponsibilityPanel.reviewConsequence.${action}`, { key: obligationKey }),
      submit: t(`operatorResponsibilityPanel.reviewNoteSubmit.${action}`),
    };
  }
  const label = approveLabel(t, stage, withChanges);
  return {
    title: label,
    consequence: withChanges
      ? t("operatorResponsibilityPanel.reviewConsequence.approveWithChanges", { key: obligationKey })
      : t(`operatorResponsibilityPanel.reviewConsequence.approve.${stage || "fm"}`, { key: obligationKey }),
    submit: label,
  };
};

/** Why the approve button is disabled — shown next to it. */
export const ApprovalBlockHint = ({ obligation }: { obligation: any }) => {
  const { t } = useTranslation();
  const { stage, block } = useFourEyes(obligation);
  if (!block || block === "alreadyConfirmed" || !stage) return null;
  return (
    <p className="min-w-0 break-words text-xs text-muted-foreground">
      {t(`operatorResponsibilityPanel.fourEyes.block.${block}`, {
        role: t(`operatorResponsibilityPanel.fourEyes.role.${stage}`),
      })}
    </p>
  );
};

/** Recht ✓ / FM ✓ trail of a version, or the note that it is an Altfreigabe. */
export const ApprovalTrail = ({ obligation }: { obligation: any }) => {
  const { t } = useTranslation();
  if (!obligation) return null;
  const approvals = obligation.approvals || {};
  if (obligation.reviewStatus === "confirmed" && !approvals.legal && !approvals.fm) {
    return (
      <p className="min-w-0 break-words text-xs text-muted-foreground">
        {t("operatorResponsibilityPanel.fourEyes.legacy", {
          reviewer: obligation.confirmedBy || "—",
          date: formatDateTime(obligation.confirmedAt),
        })}
      </p>
    );
  }
  return (
    <ul className="m-0 flex list-none flex-col gap-1 p-0 text-xs">
      {(["legal", "fm"] as ReviewerRole[]).map((role) => {
        const approval = approvals[role];
        return (
          <li key={role} className="min-w-0 break-words">
            <span className="font-semibold">{t(`operatorResponsibilityPanel.fourEyes.role.${role}`)}: </span>
            {approval ? (
              <span className="text-success">
                {t("operatorResponsibilityPanel.fourEyes.approvedBy", {
                  reviewer: approval.by,
                  date: formatDateTime(approval.at),
                })}
              </span>
            ) : (
              <span className="text-muted-foreground">{t("operatorResponsibilityPanel.fourEyes.open")}</span>
            )}
          </li>
        );
      })}
    </ul>
  );
};
